// Copyright (c) Bentley Systems
import { EtObjectType } from "./et-object-type";

export type TreeNodeViewFlags = {
  hasChildren: boolean;
  isLoaded: boolean;
  loadedWithoutSearch?: boolean;
  isExpanded?: boolean;
  isSelected?: boolean;
  isLoading?: boolean;
};

export type TreeNodeViewModel = {
  id: string;
  label: string;
  sublabel?: string;
  icon?: string;
  iconBase64Str?: string;
  children: TreeNodeViewModel[];
  flags: TreeNodeViewFlags;
};

export type TreeNodeViewState = {
  expandedNodeIds?: Record<string, boolean>;
  loadingNodeIds?: Record<string, boolean>;
  selectedNodeId?: string | null;
};

export type TreeNodeData = {
  id: string;
  label: string;
  sublabel?: string;
  objectTypes?: EtObjectType[];
  icon?: string;
  iconBase64Str?: string;
  actionIds?: number[];
  subItems: TreeNodeData[];
  hasChildren: boolean;
  isLoaded: boolean;
  loadedWithoutSearch?: boolean;
};

export type TreeNodeViewModelShallow = Omit<TreeNodeViewModel, "children">;

/**
 * Builds the view model for a single node WITHOUT recursing into its descendants.
 * Use this in per-node renderers (e.g. iTwinUI `Tree` `nodeRenderer`, which is invoked
 * once per visible node): recursing the whole subtree on every node render is O(N²) work
 * and re-runs on every selection/expand toggle (HIGH_017).
 */
export const toTreeNodeViewModelShallow = (
  node: TreeNodeData,
  viewState: TreeNodeViewState = {}
): TreeNodeViewModelShallow => {
  const {
    expandedNodeIds = {},
    loadingNodeIds = {},
    selectedNodeId = null,
  } = viewState;

  return {
    id: node.id,
    label: node.label,
    sublabel: node.sublabel,
    icon: node.icon,
    iconBase64Str: node.iconBase64Str,
    flags: {
      hasChildren: node.hasChildren,
      isLoaded: node.isLoaded,
      loadedWithoutSearch: node.loadedWithoutSearch,
      isExpanded: expandedNodeIds[node.id] === true,
      isSelected: selectedNodeId === node.id,
      isLoading: loadingNodeIds[node.id] === true,
    },
  };
};

export const toTreeNodeViewModel = (
  node: TreeNodeData,
  viewState: TreeNodeViewState = {}
): TreeNodeViewModel => ({
  ...toTreeNodeViewModelShallow(node, viewState),
  children: node.subItems.map((childNode) => toTreeNodeViewModel(childNode, viewState)),
});

export const toTreeNodeViewModels = (
  nodes: TreeNodeData[],
  viewState: TreeNodeViewState = {}
): TreeNodeViewModel[] => nodes.map((node) => toTreeNodeViewModel(node, viewState));
